import {
	type AdminPost,
	createAdminPost,
	type PostFormData,
	updateAdminPost,
	uploadNavigationPreview,
} from "@/entities/admin-post"
import { completeMediaUpload, presignUpload } from "@/shared/api/media-upload"
import { uploadFileToS3 } from "@/shared/api/upload-to-s3"
import type { StagedMedia } from "./staged-media"

/**
 * Пост сохранён, а файлы загрузились не все. Повторять создание нельзя: появится
 * второй пост. Продолжать нужно в его редактировании.
 */
export class PostSavedWithUploadError extends Error {
	public readonly postId: string
	public readonly uploadError: unknown

	public constructor(postId: string, uploadError: unknown) {
		super(uploadError instanceof Error ? uploadError.message : "Не все файлы загрузились")
		this.name = "PostSavedWithUploadError"
		this.postId = postId
		this.uploadError = uploadError
	}
}

export type SaveProgress = (percent: number, message: string) => void

export interface SavePostInput {
	data: PostFormData
	media: StagedMedia[]
	cover: File | null
	/** Сколько файлов уже есть у поста: новые встают после них. */
	existingMediaCount?: number
}

function fields(data: PostFormData) {
	return {
		title: data.title.trim(),
		shortDescription: data.shortDescription.trim() || undefined,
		description: data.description || undefined,
	}
}

/** Новый пост всегда черновик: где он выйдет, решается на экране публикации. */
export async function createPost(input: SavePostInput, onProgress: SaveProgress): Promise<AdminPost> {
	onProgress(5, "Сохраняем материал…")
	const post = await createAdminPost({
		...fields(input.data),
		formatIds: input.data.formatIds.join(",") || undefined,
		nicheIds: input.data.nicheIds.join(",") || undefined,
		topicIds: input.data.topicIds.join(",") || undefined,
		durationIds: input.data.durationIds.join(",") || undefined,
		isActive: true,
	})
	await uploadAttachments(post.id, input, onProgress)
	return post
}

export async function updatePost(
	postId: string,
	input: SavePostInput,
	onProgress: SaveProgress,
): Promise<AdminPost> {
	onProgress(5, "Сохраняем изменения…")
	const post = await updateAdminPost({
		id: postId,
		...fields(input.data),
		formatIds: input.data.formatIds,
		nicheIds: input.data.nicheIds,
		topicIds: input.data.topicIds,
		durationIds: input.data.durationIds,
	})
	await uploadAttachments(postId, input, onProgress)
	return post
}

async function uploadAttachments(postId: string, input: SavePostInput, onProgress: SaveProgress) {
	try {
		if (input.cover) {
			onProgress(15, "Загружаем обложку…")
			await uploadNavigationPreview(postId, input.cover)
		}
		if (input.media.length > 0) {
			await uploadMedia(postId, input.media, input.existingMediaCount ?? 0, onProgress)
		}
	} catch (error) {
		throw new PostSavedWithUploadError(postId, error)
	}
	onProgress(100, "Готово")
}

/**
 * presign → PUT каждого файла в S3 → регистрация. Файлы регистрируются в API только
 * после того, как прошли все PUT: неудачная загрузка не оставляет в посте битых файлов.
 */
async function uploadMedia(
	postId: string,
	media: StagedMedia[],
	offset: number,
	onProgress: SaveProgress,
) {
	onProgress(20, "Готовим файлы…")
	const toUpload = media.flatMap((item, index) => {
		const order = offset + index
		const main = {
			file: item.file,
			meta: {
				filename: item.file.name,
				mimeType: item.file.type,
				size: item.file.size,
				type: item.kind,
				order,
				width: item.width,
				height: item.height,
				duration: item.duration,
			},
		}
		if (item.kind !== "video" || !item.poster) return [main]
		return [
			main,
			{
				file: item.poster.file,
				meta: {
					filename: item.poster.file.name,
					mimeType: item.poster.file.type,
					size: item.poster.file.size,
					type: "video_thumb",
					order,
					width: item.poster.width,
					height: item.poster.height,
				},
			},
		]
	})

	const { uploads } = await presignUpload(
		postId,
		toUpload.map((item) => item.meta),
	)

	for (const [index, upload] of uploads.entries()) {
		const local = toUpload.find(
			(item) =>
				item.meta.filename === upload.filename &&
				item.meta.size === upload.size &&
				item.meta.type === upload.type &&
				item.meta.order === upload.order,
		)
		if (!local) throw new Error(`Не удалось сопоставить файл ${upload.filename}`)
		await uploadFileToS3(upload.url, local.file, (percent) =>
			onProgress(
				Math.round(25 + ((index + percent / 100) / uploads.length) * 65),
				`Загружаем ${local.file.name}: ${percent}%`,
			),
		)
	}

	onProgress(93, "Завершаем загрузку…")
	await completeMediaUpload(
		postId,
		uploads.map((upload) => ({
			s3Key: upload.s3Key,
			type: upload.type,
			filename: upload.filename,
			mimeType: upload.mimeType,
			size: upload.size,
			order: upload.order,
			width: upload.width,
			height: upload.height,
			duration: upload.duration,
		})),
	)
}
